// herkunftBezug.ts — wer den Kontakt gebracht hat: Empfehlungsgeber und
// Tippgeber/Vermittler als ECHTE Verknüpfung (Teil C, 23.09.2026).
//
// Bis dahin wählte die Anlage den Empfehlungsgeber zwar aus, gespeichert wurde
// aber nur das Wort „Empfehlungsgeber" in `Contact.kontaktherkunft` — die Person
// selbst ging verloren. Jetzt wird nach dem Anlegen
//   1. eine Beziehung zwischen beiden Kontakten angelegt (in beiden Akten unter
//      „Verknüpfungen" sichtbar) und
//   2. auf Wunsch der Zusatz-Status am Empfehlungsgeber ergänzt
//      (`POST /contact/:id/zusatzstatus`, kuratiert — bleibt auch, wenn die
//      Beziehung später gelöscht wird).
//
// Die Auswahl liegt bis zum Speichern als JSON im Formular
// (`herkunftEmpfehlungsgeber`, `herkunftTippgeber`).
//
// Seit T-324 (15.10.2026) darf die Auswahl auch ein Kontakt sein, den es noch
// NICHT gibt („neu eintippen", wie beim Arbeitgeber): Er wird beim Speichern
// zuerst angelegt — mit dem Status des Feldes und der Verantwortung (FO/BO)
// des empfohlenen Kontakts — und dann verknüpft.

import { apiUrl } from "../../utils/api";
import { flattenRelations } from "../../utils/contactToRelationItems";
import type { KontaktTreffer } from "../../services/kontaktSucheService";
import { HERKUNFT_BEZIEHUNGSTYPEN } from "../../data/herkunftBeziehungstypen";
import { refDisplayName, refHasName, type ContactRef } from "../relationsInline/types";
import { createContactFromRef } from "../relationsInline/persistRelations";

export type HerkunftArt = "Empfehlungsgeber" | "Tippgeber";

/**
 * Wer den Kontakt gebracht hat: ein bestehender Kontakt (`treffer`) ODER ein
 * neu eingetippter (`neu`, T-324), der erst beim Speichern entsteht.
 */
export type HerkunftBezug =
  | {
      treffer: KontaktTreffer;
      neu?: undefined;
      /** Den gewählten Kontakt mit dem passenden Zusatz-Status führen. */
      alsStatus: boolean;
    }
  | {
      neu: ContactRef;
      treffer?: undefined;
      /** Ohne Wirkung: Der neue Kontakt bekommt den Status gleich bei der Anlage. */
      alsStatus: boolean;
    };

/** Anzeigename der Auswahl — auch für den noch nicht angelegten Kontakt. */
export const bezugName = (b: HerkunftBezug): string => (b.treffer ? b.treffer.displayName : refDisplayName(b.neu));

/**
 * Legt den neu eingetippten Kontakt an. Status = der des Feldes; die
 * Verantwortung (FO, alle FO, BO, Inhouse) übernimmt der Server vom
 * empfohlenen Kontakt (`verantwortungVonKontaktId`) — wie bei den übrigen
 * Kontakten, die aus einer Akte heraus entstehen.
 */
export type HerkunftAnlegen = (ref: ContactRef, statusName: string, verantwortungVon: number) => Promise<number | undefined>;
const anlegenStandard: HerkunftAnlegen = (ref, statusName, von) => createContactFromRef(ref, {}, statusName, von);

export const HERKUNFT_BEZUG: Record<
  HerkunftArt,
  {
    /** Formularfeld mit der Auswahl. */
    feld: string;
    /** Überschrift des Suchfelds. */
    suchLabel: string;
    /** Zusatz-Status am gewählten Kontakt (Katalogname). */
    status: string;
    /** Wie der Status in der Checkbox heißt. */
    statusText: string;
    /** Beziehung: was der gewählte Kontakt (dest) für den neuen (src) ist … */
    relationType: string;
    /** … und umgekehrt. */
    reverseRelationType: string;
  }
> = {
  Empfehlungsgeber: {
    feld: "herkunftEmpfehlungsgeber",
    suchLabel: "Empfehlungsgeber",
    status: "Empfehlungsgeber",
    statusText: "als Empfehlungsgeber führen",
    relationType: HERKUNFT_BEZIEHUNGSTYPEN.empfehlungsgeber,
    reverseRelationType: HERKUNFT_BEZIEHUNGSTYPEN.empfohlen,
  },
  Tippgeber: {
    feld: "herkunftTippgeber",
    suchLabel: "Tippgeber/Vermittler",
    status: "Vermittler, Tippgeber",
    statusText: "als Vermittler/Tippgeber führen",
    relationType: HERKUNFT_BEZIEHUNGSTYPEN.tippgeber,
    reverseRelationType: HERKUNFT_BEZIEHUNGSTYPEN.vermittelt,
  },
};

export const HERKUNFT_ARTEN: readonly HerkunftArt[] = ["Empfehlungsgeber", "Tippgeber"];

export type HerkunftBestand = { contactId: number; displayName: string };

/** Gespeicherte Zuordnungen anzeigen, ohne sie erneut als neue Auswahl zu speichern. */
export function herkunftBestandFelder(contact: Record<string, any>): Record<string, string> {
  const beziehungen = flattenRelations(contact);
  return Object.fromEntries(HERKUNFT_ARTEN.map((art) => {
    const cfg = HERKUNFT_BEZUG[art];
    const kontakte = new Map<number, HerkunftBestand>();
    for (const r of beziehungen) {
      const passt = r.srcId === contact.id
        ? r.relType === cfg.relationType
        : r.relType === cfg.reverseRelationType || r.reverse === cfg.relationType;
      if (!passt) continue;
      const p = r.other;
      kontakte.set(p.id, {
        contactId: p.id,
        displayName: p.displayName || [p.lastname, p.firstName].filter(Boolean).join(", ") || "Unbenannter Kontakt",
      });
    }
    return [`${cfg.feld}Bestand`, JSON.stringify([...kontakte.values()])];
  }));
}

export function herkunftBestandLesen(raw: unknown): HerkunftBestand[] {
  if (typeof raw !== "string") return [];
  try {
    const liste = JSON.parse(raw);
    return Array.isArray(liste)
      ? liste.filter((p) => p && Number.isInteger(p.contactId) && p.contactId > 0 && typeof p.displayName === "string")
      : [];
  } catch {
    return [];
  }
}


export function bezugLesen(raw: unknown): HerkunftBezug | null {
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    const p = JSON.parse(raw) as { treffer?: KontaktTreffer; neu?: ContactRef; alsStatus?: boolean };
    // Neu eingetippt: auch ohne Namen gültig — sonst sprünge das Feld beim
    // ersten Zeichen zurück auf die Suche. Den Namen prüft das Speichern.
    if (p?.neu && typeof p.neu === "object") {
      return { neu: { ...p.neu, type: p.neu.type === "Firma" ? "Firma" : "Person", manual: true }, alsStatus: true };
    }
    const id = Number(p?.treffer?.contactId);
    if (!p?.treffer || !Number.isFinite(id) || id <= 0) return null;
    return { treffer: p.treffer as KontaktTreffer, alsStatus: p.alsStatus !== false };
  } catch {
    return null;
  }
}

export const bezugSchreiben = (b: HerkunftBezug | null): string => (b ? JSON.stringify(b) : "");

/** Hat der Kontakt den Status schon — als Primär- oder als Zusatz-Status? */
export const hatStatus = (primaer: string | null | undefined, extra: readonly string[] | undefined, name: string): boolean =>
  primaer === name || (extra ?? []).includes(name);

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** Fehlschlag mit der Begründung des Servers (NestJS: `message` als Text oder Liste). */
class Fehlschlag extends Error {
  constructor(readonly grund: string | null) {
    super(grund ?? "Fehlschlag");
  }
}

/** Liest die Begründung aus einer Fehlerantwort; null, wenn keine lesbar ist. */
export async function serverGrund(res: Response): Promise<string | null> {
  const body = (await res.json().catch(() => null)) as { message?: unknown } | null;
  const m = body?.message;
  if (typeof m === "string" && m.trim()) return m.trim();
  if (Array.isArray(m)) {
    const teile = m.filter((x): x is string => typeof x === "string" && !!x.trim()).map((x) => x.trim());
    if (teile.length) return teile.join("; ");
  }
  return null;
}

const mitGrund = (text: string, e: unknown): string =>
  e instanceof Fehlschlag && e.grund ? `${text} (Grund: ${e.grund})` : text;

/**
 * Nach dem Anlegen: Beziehung und — falls gewünscht — Status je gewählter
 * Herkunft. Liefert die Hinweise zu allem, was NICHT geklappt hat; der eben
 * angelegte Kontakt bleibt in jedem Fall bestehen.
 */
export async function herkunftVerknuepfen(
  neuerKontaktId: number,
  formFields: Record<string, unknown>,
  auswahl: readonly string[],
  fetchFn: Fetch = (i, init) => fetch(i, init),
  anlegen: HerkunftAnlegen = anlegenStandard,
): Promise<string[]> {
  const probleme: string[] = [];
  for (const art of HERKUNFT_ARTEN) {
    if (!auswahl.includes(art)) continue;
    const bezug = bezugLesen(formFields[HERKUNFT_BEZUG[art].feld]);
    if (!bezug) continue;
    const cfg = HERKUNFT_BEZUG[art];
    const name = bezugName(bezug);
    let zielId: number;
    if (bezug.neu) {
      // T-324: erst anlegen, dann verknüpfen. Ohne Namen gibt es nichts
      // anzulegen — das wird gesagt, statt die Auswahl still zu verlieren.
      if (!refHasName(bezug.neu)) {
        const fehlt = bezug.neu.type === "Firma" ? "Firmenname" : "Nachname";
        probleme.push(`Der neue ${cfg.suchLabel} wurde nicht angelegt — es fehlt der ${fehlt}.`);
        continue;
      }
      try {
        const id = await anlegen(bezug.neu, cfg.status, neuerKontaktId);
        if (!id) throw new Fehlschlag(null);
        zielId = id;
      } catch (e) {
        const grund = e instanceof Fehlschlag ? e : new Fehlschlag(e instanceof Error && e.message.trim() ? e.message.trim() : null);
        probleme.push(mitGrund(`${name} konnte nicht als ${cfg.suchLabel} angelegt werden.`, grund));
        continue;
      }
    } else {
      zielId = bezug.treffer.contactId;
    }
    try {
      const res = await fetchFn(apiUrl("contact-relation"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contactSrcId: neuerKontaktId,
          contactDestId: zielId,
          relationType: cfg.relationType,
          reverseRelationType: cfg.reverseRelationType,
        }),
      });
      if (!res.ok) throw new Fehlschlag(await serverGrund(res));
    } catch (e) {
      probleme.push(
        mitGrund(
          bezug.neu
            ? `${name} ist angelegt, konnte aber nicht als ${cfg.suchLabel} verknüpft werden.`
            : `${name} konnte nicht als ${cfg.suchLabel} verknüpft werden.`,
          e,
        ),
      );
      continue;
    }
    // Der neue Kontakt trägt den Status schon seit der Anlage.
    if (bezug.neu || !bezug.alsStatus) continue;
    try {
      const res = await fetchFn(apiUrl(`contact/${zielId}/zusatzstatus`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ statusName: cfg.status }),
      });
      if (!res.ok) throw new Fehlschlag(await serverGrund(res));
    } catch (e) {
      probleme.push(
        mitGrund(
          `${name} ist verknüpft, der Status „${cfg.status}" konnte aber nicht gesetzt werden `
            + "(evtl. fehlt das Recht, diesen Kontakt zu bearbeiten).",
          e,
        ),
      );
    }
  }
  return probleme;
}
